import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, LogOut } from "lucide-react";
import { useStudentAuth } from "./StudentAuth";

const NAV = [
  { to: "/student/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/student/profile", label: "Profile", icon: "User" },
  { to: "/student/program", label: "My Program", icon: "GraduationCap" },
  { to: "/student/classes", label: "Classes", icon: "Video" },
  { to: "/student/materials", label: "Study Materials", icon: "BookOpen" },
  { to: "/student/assignments", label: "Assignments", icon: "ClipboardList" },
  { to: "/student/projects", label: "Projects", icon: "FolderKanban" },
  { to: "/student/attendance", label: "Attendance", icon: "CalendarCheck" },
  { to: "/student/payments", label: "Payments", icon: "CreditCard" },
  { to: "/student/certificates", label: "Certificates", icon: "Award" },
  { to: "/student/announcements", label: "Announcements", icon: "Megaphone" },
  { to: "/student/support", label: "Support", icon: "LifeBuoy" },
];

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

export const StudentLayout = ({ children }) => {
  const { user, logout } = useStudentAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/student/login"); };

  const linkCls = ({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-orange text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`;

  const Inner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-5 py-5">
        <span className="font-heading text-2xl font-bold text-white" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>ASAI</span>
        <span className="rounded bg-orange/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange">Student</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`stunav-${n.to.split("/").pop()}`} onClick={() => setOpen(false)}>
            <Icon name={n.icon} className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="mb-3 truncate text-xs text-slate-400">{user?.email}</div>
        <button onClick={doLogout} data-testid="student-logout" className="flex w-full items-center gap-2 rounded-lg bg-white/5 px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/10">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: "Satoshi, sans-serif" }}>
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-navy lg:block">{Inner}</aside>
      {open && (<><div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} /><aside className="fixed inset-y-0 left-0 z-50 w-64 bg-navy lg:hidden">{Inner}</aside></>)}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-8">
          <button onClick={() => setOpen(true)} className="rounded-lg p-2 text-slate-700 lg:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          <div className="hidden text-sm font-medium text-slate-500 lg:block">Student Portal</div>
          <div className="text-sm text-slate-600">{user?.name}</div>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
